import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge, Alert } from '@components';
import { Wallet } from '../types';

interface WalletBalanceProps {
  wallet: Wallet | null;
  loading?: boolean;
  onRefresh: () => void;
  lowBalanceThreshold?: number;
}

export const WalletBalance: React.FC<WalletBalanceProps> = ({
  wallet,
  loading = false,
  onRefresh,
  lowBalanceThreshold = 20,
}) => {
  const { t } = useTranslation();
  const balance = wallet
    ? (() => {
        const parsed = parseFloat(wallet.balance);
        return isNaN(parsed) ? 0 : parsed;
      })()
    : 0;
  const isLowBalance = balance < lowBalanceThreshold;
  const currency = wallet?.currency || 'EUR';

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('billing.currentBalance')}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-2"
        >
          <span className={`material-icons ${loading ? 'animate-spin' : ''}`}>refresh</span>
          {t('common.refresh')}
        </Button>
      </div>

      {isLowBalance && (
        <Alert variant="warning" title={t('billing.lowBalanceWarningTitle')}>
          {t('billing.lowBalanceWarningMessage', { threshold: lowBalanceThreshold })}
        </Alert>
      )}

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                {t('billing.currentBalance')}
              </div>
              <div className="text-4xl font-bold text-gray-900 dark:text-white">
                {currency} {isNaN(balance) ? '0.00' : balance.toFixed(2)}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Badge
                  variant={wallet?.is_active ? 'success' : 'secondary'}
                  size="sm"
                  className="mt-1"
                >
                  {wallet?.is_active ? t('common.active') : t('common.inactive')}
                </Badge>
                {wallet && wallet.id && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t('billing.walletId')}:{' '}
                    {typeof wallet.id === 'string'
                      ? wallet.id.slice(0, 8)
                      : String(wallet.id).slice(0, 8)}
                    ...
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <span className="material-icons text-6xl text-primary-500 opacity-20">
                account_balance_wallet
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
